import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import SaveStrategyModal from './SaveStrategyModal';
import StrategyService from '../../services/StrategyService';
import { StrategyData } from './StrategyTypes';

// Difficulty options
const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', color: 'bg-green-600' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-600' },
  { value: 'hard', label: 'Hard', color: 'bg-orange-600' },
  { value: 'very hard', label: 'Very Hard', color: 'bg-red-600' },
];

// Cost options
const COST_OPTIONS = [
  { value: 'cheap', label: 'Cheap', color: 'bg-green-600' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-600' },
  { value: 'expensive', label: 'Expensive', color: 'bg-red-600' },
];

const Strategy: React.FC<{ _onOpenManageStrategies?: () => void }> = ({ _onOpenManageStrategies }) => {
  // State
  const [strategy, setStrategy] = useState<StrategyData>({
    name: '',
    difficulty: 'medium',
    cost: 'medium',
    description: '',
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [savedStrategies, setSavedStrategies] = useState<{ [key: string]: string }>({});
  const [notification, setNotification] = useState<string | null>(null);
  
  // Load saved data from localStorage on component mount
  useEffect(() => {
    try {
      const savedStrategy = localStorage.getItem('atlasStrategy');
      if (savedStrategy) {
        const parsedStrategy = JSON.parse(savedStrategy);
        setStrategy(parsedStrategy);
      }
      
      // Load saved strategies
      const strategies = StrategyService.getSavedStrategies();
      setSavedStrategies(strategies);
    } catch (err) {
      console.error('Failed to load strategy:', err);
    }
    setLoaded(true);
  }, []);

  // Save strategy to localStorage function
  const saveToLocalStorage = (data: StrategyData) => {
    try {
      const stringified = JSON.stringify(data);
      localStorage.setItem('atlasStrategy', stringified);
    } catch (err) {
      console.error('Failed to save strategy:', err);
    }
  };

  // Save data to localStorage whenever it changes, but only after initial load
  useEffect(() => {
    if (loaded) {
      saveToLocalStorage(strategy);
    }
  }, [strategy, loaded]);
  
  // Clear notification after timeout
  useEffect(() => {
    if (notification) {
      const timeout = setTimeout(() => {
        setNotification(null);
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [notification]);

  // Event handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setStrategy(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDifficultySelect = (difficulty: 'easy' | 'medium' | 'hard' | 'very hard') => {
    setStrategy(prev => ({
      ...prev,
      difficulty,
    }));
  };

  const handleCostSelect = (cost: 'cheap' | 'medium' | 'expensive') => {
    setStrategy(prev => ({
      ...prev,
      cost,
    }));
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
    if (!isEditing) setIsExpanded(true);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  const handleOpenSaveModal = () => {
    setIsSaveModalOpen(true);
  };
  
  const showNotification = (message: string) => {
    setNotification(message);
  };
  
  const handleSaveStrategy = (name: string) => {
    // Update strategy name
    setStrategy(prev => ({
      ...prev,
      name,
    }));
    
    // Save to localStorage for future use
    const updatedStrategy = {
      ...strategy,
      name,
    };
    saveToLocalStorage(updatedStrategy);
    
    // Save as a saved strategy
    const success = StrategyService.saveStrategy(name);
    
    if (success) {
      // Refresh saved strategies list
      const strategies = StrategyService.getSavedStrategies();
      setSavedStrategies(strategies);
      
      // Show success notification
      showNotification(`Strategy "${name}" saved successfully!`);
    } else {
      // Show error notification
      showNotification('Failed to save strategy. Please try again.');
    }
  };

  // Get truncated description for collapsed view
  const getTruncatedDescription = () => {
    if (!strategy.description) return 'No description';
    return strategy.description.length > 30
      ? `${strategy.description.substring(0, 30)}...`
      : strategy.description;
  };

  // Get difficulty and cost labels
  const difficultyLabel = DIFFICULTY_OPTIONS.find(o => o.value === strategy.difficulty)?.label || 'Medium';
  const difficultyColor = DIFFICULTY_OPTIONS.find(o => o.value === strategy.difficulty)?.color || 'bg-gray-600';
  const costLabel = COST_OPTIONS.find(o => o.value === strategy.cost)?.label || 'Medium';
  const costColor = COST_OPTIONS.find(o => o.value === strategy.cost)?.color || 'bg-gray-600';

  return (
    <div className="bg-gray-800 text-white p-4 rounded-lg border border-gray-700 relative">
      {/* Notification */}
      {notification && (
        <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-10 transition-opacity">
          {notification}
        </div>
      )}
      
      <h2 className="text-xl font-semibold mb-4">Current Strategy</h2>
      
      {isEditing ? (
        // Edit Mode - Responsive layout
        <>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium">Editing Strategy</h3>
            <div>
              <button 
                onClick={toggleEdit}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-sm"
              >
                View
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium mb-1">Strategy Name:</label>
              <input
                type="text"
                name="name"
                value={strategy.name}
                onChange={handleChange}
                placeholder="Enter strategy name"
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>

            {/* Difficulty and Cost Selector - Responsive */}
            <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
              {/* Difficulty Selector */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Difficulty:</label>
                <div className="flex flex-wrap gap-2">
                  {DIFFICULTY_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleDifficultySelect(option.value as 'easy' | 'medium' | 'hard' | 'very hard')}
                      className={`px-3 py-1 rounded text-sm ${
                        strategy.difficulty === option.value
                          ? `${option.color} font-medium`
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost Selector */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Cost:</label>
                <div className="flex flex-wrap gap-2">
                  {COST_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleCostSelect(option.value as 'cheap' | 'medium' | 'expensive')}
                      className={`px-3 py-1 rounded text-sm ${
                        strategy.cost === option.value
                          ? `${option.color} font-medium`
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Description Field */}
            <div>
              <label className="block text-sm font-medium mb-1">Description:</label>
              <textarea
                name="description"
                value={strategy.description}
                onChange={handleChange}
                placeholder="Enter strategy description using markdown..."
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white min-h-[150px]"
              />
            </div>
          </div>
        </>
      ) : (
        // View Mode - Responsive layout
        <>
          {/* Header with expand/collapse toggle */}
          <div 
            className="flex justify-between items-center cursor-pointer" 
            onClick={toggleExpand}
          >
            {/* Collapsed View - Everything in one line on larger screens, stacked on mobile */}
            {!isExpanded ? (
              <div className="flex flex-col md:flex-row md:items-center w-full overflow-hidden space-y-2 md:space-y-0">
                <h3 className="text-lg font-semibold mr-2 truncate">{strategy.name || 'Unnamed Strategy'}</h3>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${difficultyColor} text-center`}>
                    Difficulty: {difficultyLabel} 
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${costColor} text-center`}>
                    Cost: {costLabel}
                  </span>
                </div>
                <p className="text-gray-400 ml-0 md:ml-2 truncate">{getTruncatedDescription()}</p>
              </div>
            ) : (
              // Expanded Header
              <h3 className="text-lg font-semibold">{strategy.name || 'Unnamed Strategy'}</h3>
            )}
            
            <div className="flex items-center ml-2 flex-shrink-0">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEdit();
                }}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-sm mr-2"
              >
                Edit
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenSaveModal();
                }}
                className="px-3 py-1 bg-green-600 hover:bg-green-500 rounded text-sm mr-2"
              >
                Save
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded"
              >
                {isExpanded ? '▲' : '▼'}
              </button>
            </div>
          </div>

          {/* Expanded View - Vertical alignment always on mobile, horizontal on desktop */}
          {isExpanded && (
            <div className="mt-4">
              <div className="flex flex-col md:flex-row items-start">
                <div className="flex-shrink-0 mr-0 md:mr-4 mb-4 md:mb-0">
                  <div className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${difficultyColor} text-center`}>
                      Difficulty: {difficultyLabel}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${costColor} text-center`}>
                      Cost: {costLabel}
                    </span>
                  </div>
                </div>
                <div className="flex-1 prose prose-invert prose-sm max-w-none">
                  {strategy.description ? (
                    <ReactMarkdown>{strategy.description}</ReactMarkdown>
                  ) : (
                    <p className="text-gray-400 italic">No description provided.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Save Strategy Modal */}
      <SaveStrategyModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleSaveStrategy}
        currentName={strategy.name}
        existingNames={Object.keys(savedStrategies)}
      />
    </div>
  );
};

export default Strategy;